/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useEffect, useState, useCallback } from "react";
import { styled, alpha } from "@mui/material/styles";
import { Typography, Box, MenuItem, Drawer, Button } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ReactComponent as UserIcon } from "assets/icon_user.svg";
import { ReactComponent as MenuCloser } from "assets/Menu_Close.svg";
import { useAuth0 } from "@auth0/auth0-react";
import { useMapDataContext } from "context/MapDataProvider";
import { useTranslation } from "react-i18next";
import { ProjectList } from "components/Project/ProjectList";
import { ProjectInfo, useUserContext } from "context/UserContext";
import { Const } from "const";

const menuBorder = `1px solid ${alpha("#000", 0.5)}`;

interface MenuSidebarProps {
  openMenu: boolean;
  onMenuClose: () => void;
  onJobsiteChanged?: (jobsite?: ProjectInfo) => void;
}

export const MenuSidebar: FC<MenuSidebarProps> = ({
  openMenu,
  onMenuClose,
  onJobsiteChanged,
}) => {
  const [drawerWidth, setDrawerWidth] = useState(window.innerWidth / 4);
  const MapVerBox = styled(Box)({
    width: "100%",
    maxWidth: drawerWidth - 40,
    textAlign: "right",
  });
  const UserNameBox = styled(Box)(({ theme }) => ({
    width: "100%",
    maxWidth: drawerWidth - 40,
    margin: theme.spacing(1, 0, 10, 0),
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }));
  const MenuButton = styled(MenuItem)(({ theme }) => ({
    backgroundColor: Const.sidePanelFooterColor,
    borderBottom: menuBorder,
    padding: theme.spacing(2, 4),
  }));
  const BottomMenuItem = styled(MenuItem)(({ theme }) => ({
    marginTop: "auto",
    backgroundColor: Const.sidePanelFooterColor,
    padding: theme.spacing(2, 3),
    borderTop: menuBorder,
  }));
  const DrawerContents = styled(Box)({
    display: "flex",
    flexDirection: "column",
    m: "auto",
    width: drawerWidth - 10,
    height: "100%",
    backgroundColor: Const.sidePanelBodyColor,
  });
  const CloseButton = styled(Button)({
    position: "fixed",
    top: "10%",
    right: drawerWidth - 18,
  });

  const { logout } = useAuth0();
  const { mapData } = useMapDataContext();
  const { jobsite, firstName, lastName } = useUserContext();
  const { t } = useTranslation();

  const [userDisp, setUserDisp] = useState("");
  const [isOpenSelectJobsite, setIsOpenSelectJobsite] = useState(false);

  const handleSelectProject = useCallback(
    (site?: ProjectInfo) => {
      if (site !== jobsite) {
        onJobsiteChanged?.(site);
        // Close Project selector dialog
        setIsOpenSelectJobsite(false);
      }
    },
    [jobsite, onJobsiteChanged]
  );

  useEffect(() => {
    if (openMenu) {
      setDrawerWidth(window.innerWidth / 4);
      setUserDisp(`${firstName} ${lastName}`);
    }
  }, [openMenu, firstName, lastName]);

  return (
    <>
      <Drawer anchor="right" onClose={onMenuClose} open={openMenu}>
        <CloseButton onClick={onMenuClose}>
          <MenuCloser />
        </CloseButton>
        <DrawerContents>
          <MapVerBox>
            {`${t("AWVUI-MSG144")}:${
              mapData?.mapVer?.toString() ?? t("AWVUI-MSG143")
            }`}
          </MapVerBox>
          <UserNameBox>
            <UserIcon />
            <Typography variant="h6">{userDisp}</Typography>
          </UserNameBox>
          <Box borderTop={menuBorder}>
            <MenuButton
              onClick={() => {
                setIsOpenSelectJobsite(true);
              }}
            >
              {t("AWVUI-MSG145")}
            </MenuButton>
            <MenuButton
              onClick={() => {
                window.open(`/userguide?jobSiteId=${jobsite?.jobSiteId}`);
              }}
            >
              {t("AWVUI-MSG146")}&nbsp;
              <OpenInNewIcon />
            </MenuButton>
            <MenuButton
              onClick={() => {
                window.open(`/about?jobSiteId=${jobsite?.jobSiteId}`);
              }}
            >
              {t("AWVUI-MSG147")}&nbsp;
              <OpenInNewIcon />
            </MenuButton>
          </Box>
          <BottomMenuItem
            onClick={() => {
              logout();
            }}
          >
            {t("AWVUI-MSG69")}
          </BottomMenuItem>
        </DrawerContents>
      </Drawer>
      {isOpenSelectJobsite && (
        <ProjectList
          open={isOpenSelectJobsite}
          onSelected={handleSelectProject}
        />
      )}
    </>
  );
};
